import { useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Upload, Image, Trophy, X, Info } from 'lucide-react';
import { toast } from 'sonner';
import { validateImageFile } from './constants';

const API = `${process.env.REACT_APP_BACKEND_URL}`;

export const TeamIdentitySection = ({
  teamName,
  setTeamName,
  badgePreview,
  setBadgePreview,
  badgeFile,
  setBadgeFile,
  headerPreview,
  setHeaderPreview,
  headerFile,
  setHeaderFile,
  dressingRoomHeaderPreview,
  setDressingRoomHeaderPreview,
  dressingRoomHeaderFile,
  setDressingRoomHeaderFile,
  setDressingRoomHeader,
  tickerEnabled,
  setTickerEnabled,
  tickerMode,
  setTickerMode,
  tickerLabel,
  setTickerLabel,
  tickerText,
  setTickerText,
  setClubBadge,
  setHeaderImage,
  currentSeasonName = null,
}) => {
  const fileInputRef = useRef(null);
  const headerInputRef = useRef(null);
  const dressingRoomHeaderInputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const validation = validateImageFile(file);
      if (!validation.valid) {
        toast.error(validation.error);
        return;
      }
      setBadgeFile(file);
      setBadgePreview(URL.createObjectURL(file));
    }
  };

  const handleHeaderFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const validation = validateImageFile(file);
      if (!validation.valid) {
        toast.error(validation.error);
        return;
      }
      setHeaderFile(file);
      setHeaderPreview(URL.createObjectURL(file));
    }
  };

  const handleRemoveBadge = () => {
    setBadgeFile(null);
    setClubBadge('');
    setBadgePreview('');
  };

  const handleRemoveHeader = () => {
    setHeaderFile(null);
    setHeaderImage('');
    setHeaderPreview('');
  };

  const handleDressingRoomHeaderFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const validation = validateImageFile(file);
      if (!validation.valid) {
        toast.error(validation.error);
        return;
      }
      setDressingRoomHeaderFile(file);
      setDressingRoomHeaderPreview(URL.createObjectURL(file));
    }
  };

  const handleRemoveDressingRoomHeader = () => {
    setDressingRoomHeaderFile(null);
    setDressingRoomHeader('');
    setDressingRoomHeaderPreview('');
  };

  return (
    <Card data-testid="team-identity-card">
      <CardHeader>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <CardTitle className="text-xl font-semibold font-title flex items-center gap-2">
            <Trophy className="w-5 h-5 text-emerald-600" strokeWidth={1.5} />
            Team Identity
          </CardTitle>
          {currentSeasonName && (
            <span
              className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 text-emerald-800 px-2.5 py-1 text-xs font-semibold"
              data-testid="team-identity-season-pill"
              title="You are editing this season's team identity"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              Season: {currentSeasonName}
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 flex items-center gap-2 text-xs text-emerald-900" data-testid="team-identity-season-banner">
          <Info className="w-4 h-4 text-emerald-600 shrink-0" />
          <span>Editing team identity for the currently-selected season only. Switch seasons in the top-bar to edit a different one — each carries its own team name, badge and match defaults.</span>
        </div>
        {/* Club Badge */}
        <div className="space-y-4">
          <Label className="text-base">Club Badge</Label>
          <div className="flex items-start gap-6">
            <div className="flex-shrink-0">
              {badgePreview ? (
                <img 
                  src={badgePreview.startsWith('/api') ? `${API}${badgePreview}` : badgePreview} 
                  alt="Club Badge Preview"
                  className="badge-preview w-24 h-24"
                  onError={() => setBadgePreview('')}
                />
              ) : (
                <div className="w-24 h-24 rounded-xl bg-gray-100 flex items-center justify-center border-2 border-dashed border-gray-300">
                  <Image className="w-10 h-10 text-gray-400" strokeWidth={1.5} />
                </div>
              )}
            </div>
            <div className="flex-1 space-y-3">
              <div>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  className="hidden"
                />
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    className="rounded-xl"
                    data-testid="upload-badge-btn"
                  >
                    <Upload className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    {badgeFile ? 'Change Image' : 'Upload Image'}
                  </Button>
                  {(badgePreview || badgeFile) && (
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={handleRemoveBadge}
                      className="text-red-500 hover:text-red-600 hover:bg-red-50"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">Max 5MB. JPEG, PNG, GIF, or WebP</p>
                {badgeFile && <p className="text-xs text-emerald-600 mt-1">{badgeFile.name}</p>}
              </div>
            </div>
          </div>
        </div>

        {/* Team Name */}
        <div className="space-y-2">
          <Label htmlFor="teamName" className="text-base">Team Name</Label>
          <Input
            id="teamName"
            value={teamName}
            onChange={(e) => setTeamName(e.target.value)}
            placeholder="Enter your team name"
            className="rounded-xl"
            data-testid="team-name-input"
          />
          <p className="text-xs text-gray-500">This will appear in the sidebar and mobile header.</p>
        </div>

        {/* Header Image */}
        <div className="space-y-4">
          <Label className="text-base">Clubhouse Header Image</Label>
          <p className="text-xs text-gray-500 -mt-2">This image will appear at the top of your Clubhouse page.</p>
          
          {headerPreview ? (
            <div className="space-y-3">
              <div className="relative rounded-xl overflow-hidden">
                <img 
                  src={headerPreview.startsWith('/api') ? `${API}${headerPreview}` : headerPreview} 
                  alt="Header Preview" 
                  className="w-full h-32 object-cover"
                  onError={() => setHeaderPreview('')}
                />
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={handleRemoveHeader}
                  className="absolute top-2 right-2 rounded-lg"
                  data-testid="remove-header-btn"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              {headerFile && <p className="text-xs text-emerald-600">{headerFile.name}</p>}
              <input
                type="file"
                ref={headerInputRef}
                onChange={handleHeaderFileChange}
                accept="image/jpeg,image/png,image/gif,image/webp"
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => headerInputRef.current?.click()}
                className="rounded-xl"
              >
                <Upload className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Change Image
              </Button>
            </div>
          ) : (
            <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center">
              <Image className="w-10 h-10 text-gray-400 mx-auto mb-3" strokeWidth={1.5} />
              <p className="text-sm text-gray-500 mb-3">No header image uploaded</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <input
                  type="file"
                  ref={headerInputRef}
                  onChange={handleHeaderFileChange}
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => headerInputRef.current?.click()}
                  className="rounded-xl"
                  data-testid="upload-header-btn"
                >
                  <Upload className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  Upload Header
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-2">Max 5MB. JPEG, PNG, GIF, or WebP. Recommended: 1200x300px</p>
            </div>
          )}
        </div>

        {/* Dressing Room Header Image */}
        <div className="space-y-4">
          <Label className="text-base">Dressing Room Header Image</Label>
          <p className="text-xs text-gray-500 -mt-2">This image will appear at the top of your Dressing Room page.</p>

          {dressingRoomHeaderPreview ? (
            <div className="space-y-3">
              <div className="relative rounded-xl overflow-hidden">
                <img
                  src={dressingRoomHeaderPreview.startsWith('/api') ? `${API}${dressingRoomHeaderPreview}` : dressingRoomHeaderPreview}
                  alt="Dressing Room Header Preview"
                  className="w-full h-32 object-cover"
                  onError={() => setDressingRoomHeaderPreview('')}
                />
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={handleRemoveDressingRoomHeader}
                  className="absolute top-2 right-2 rounded-lg"
                  data-testid="remove-dressing-room-header-btn"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              {dressingRoomHeaderFile && <p className="text-xs text-emerald-600">{dressingRoomHeaderFile.name}</p>}
              <input
                type="file"
                ref={dressingRoomHeaderInputRef}
                onChange={handleDressingRoomHeaderFileChange}
                accept="image/jpeg,image/png,image/gif,image/webp"
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => dressingRoomHeaderInputRef.current?.click()}
                className="rounded-xl"
              >
                <Upload className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Change Image
              </Button>
            </div>
          ) : (
            <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center">
              <Image className="w-10 h-10 text-gray-400 mx-auto mb-3" strokeWidth={1.5} />
              <p className="text-sm text-gray-500 mb-3">No header image uploaded</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <input
                  type="file"
                  ref={dressingRoomHeaderInputRef}
                  onChange={handleDressingRoomHeaderFileChange}
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => dressingRoomHeaderInputRef.current?.click()}
                  className="rounded-xl"
                  data-testid="upload-dressing-room-header-btn"
                >
                  <Upload className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  Upload Header
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-2">Max 5MB. JPEG, PNG, GIF, or WebP. Recommended: 1200x300px</p>
            </div>
          )}
        </div>

        {/* Breaking News Ticker */}
        <div className="space-y-4 pt-4 border-t">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base">Breaking News Ticker</Label>
              <p className="text-xs text-gray-500">Display a scrolling news ticker on the Clubhouse header</p>
            </div>
            <Switch
              checked={tickerEnabled}
              onCheckedChange={setTickerEnabled}
              data-testid="ticker-toggle"
            />
          </div>
          
          {tickerEnabled && (
            <div className="space-y-4 pl-4 border-l-2 border-emerald-200">
              {/* Mode Selection */}
              <div className="space-y-2">
                <Label className="text-sm">Headline Source</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={tickerMode === 'manual' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setTickerMode('manual')}
                    className={`rounded-xl ${tickerMode === 'manual' ? 'btn-primary' : ''}`}
                    data-testid="ticker-mode-manual"
                  >
                    Manual
                  </Button>
                  <Button
                    type="button"
                    variant={tickerMode === 'ai' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setTickerMode('ai')}
                    className={`rounded-xl ${tickerMode === 'ai' ? 'btn-primary' : ''}`}
                    data-testid="ticker-mode-ai"
                  >
                    AI Generated
                  </Button>
                </div>
                <p className="text-xs text-gray-500">
                  {tickerMode === 'ai' 
                    ? 'AI generates headlines from your matches, stats, and team data (refreshes daily)' 
                    : 'Enter your own custom headline message'}
                </p>
              </div>

              {/* Manual Mode Fields */}
              {tickerMode === 'manual' && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="tickerLabel" className="text-sm">Leading Text</Label>
                    <Input
                      id="tickerLabel"
                      value={tickerLabel}
                      onChange={(e) => setTickerLabel(e.target.value)}
                      placeholder="BREAKING NEWS:"
                      className="rounded-xl"
                      data-testid="ticker-label-input"
                    />
                    <p className="text-xs text-gray-500">The bold label that appears before the message</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="tickerText" className="text-sm">Ticker Message</Label>
                    <Input
                      id="tickerText"
                      value={tickerText}
                      onChange={(e) => setTickerText(e.target.value)}
                      placeholder="Enter your breaking news message here..."
                      className="rounded-xl"
                      data-testid="ticker-text-input"
                    />
                    <p className="text-xs text-gray-500">The message that scrolls across the header</p>
                  </div>
                </>
              )}

              {/* AI Mode Info */}
              {tickerMode === 'ai' && (
                <div className="bg-emerald-50 rounded-xl p-4 text-sm text-emerald-800">
                  <p className="font-medium mb-1">AI Headlines Active</p>
                  <p className="text-xs text-emerald-600">
                    Up to 6 headlines will be generated daily from your team data including fixtures, results, player stats, and more. Headlines rotate automatically every 5 seconds.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default TeamIdentitySection;
